import React, { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw, CheckCircle, XCircle, Clock, AlertCircle, ChevronRight, ChevronDown } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { schedulerApi, ExecutionDetail, ExecutionStepDetail } from '../../services/api';
import Colors, { withAlpha } from '../../constants/Colors';

type StatusFilter = 'ALL' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'TIMED_OUT' | 'ABORTED';

export default function ExecutionsPanel() {
  const [executions, setExecutions] = useState<ExecutionDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Expandable row state
  const [expandedArn, setExpandedArn] = useState<string | null>(null);
  const [stepsData, setStepsData] = useState<Record<string, ExecutionStepDetail[]>>({});
  const [stepsLoading, setStepsLoading] = useState(false);
  const [stepsError, setStepsError] = useState<string | null>(null);

  const fetchExecutions = useCallback(async () => {
    setLoading(true);
    setError(null);

    const response = await schedulerApi.listExecutions(
      statusFilter === 'ALL' ? undefined : statusFilter,
      100
    );

    console.log('Executions response:', response);

    if (response.success && response.data) {
      setExecutions(response.data.executions || []);
    } else {
      setError(response.error || 'Failed to fetch executions');
    }

    setLoading(false);
    setLastRefresh(new Date());
  }, [statusFilter]);

  // Initial fetch and when filter changes
  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  // Smart polling: only when auto-refresh is ON or there are RUNNING executions
  useEffect(() => {
    const hasRunningExecutions = executions.some(e => e.status === 'RUNNING');
    const shouldPoll = autoRefresh || hasRunningExecutions;

    if (!shouldPoll) return;

    const interval = setInterval(() => {
      fetchExecutions();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, executions, fetchExecutions]);

  const handleRowClick = async (execution: ExecutionDetail) => {
    const arn = execution.execution_arn;

    // Toggle collapse
    if (expandedArn === arn) {
      setExpandedArn(null);
      return;
    }

    setExpandedArn(arn);
    setStepsError(null);

    // Always re-fetch for RUNNING executions, use cache otherwise
    if (stepsData[arn] && execution.status !== 'RUNNING') {
      return;
    }

    setStepsLoading(true);
    const response = await schedulerApi.getExecutionHistory(arn);

    if (response.success && response.data) {
      setStepsData(prev => ({ ...prev, [arn]: response.data!.steps }));
    } else {
      setStepsError(response.error || 'Failed to fetch execution history');
    }
    setStepsLoading(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return <CheckCircle size={14} style={{ color: Colors.success }} />;
      case 'FAILED':
        return <XCircle size={14} style={{ color: Colors.error }} />;
      case 'RUNNING':
      case 'IN_PROGRESS':
        return <Clock size={14} style={{ color: '#5856D6' }} />;
      case 'TIMED_OUT':
      case 'ABORTED':
      case 'CANCELLED':
        return <AlertCircle size={14} style={{ color: '#FF9500' }} />;
      default:
        return <Clock size={14} style={{ color: 'rgba(255, 255, 255, 0.5)' }} />;
    }
  };

  const getStatusStyle = (status: string) => {
    const baseStyle = {
      padding: '4px 10px',
      borderRadius: '999px',
      fontSize: '11px',
      fontWeight: 600 as const,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      border: '1px solid',
    };

    switch (status) {
      case 'SUCCEEDED':
        return { ...baseStyle, backgroundColor: withAlpha(Colors.success, 0.2), color: Colors.success, borderColor: Colors.success };
      case 'FAILED':
        return { ...baseStyle, backgroundColor: withAlpha(Colors.error, 0.2), color: Colors.error, borderColor: Colors.error };
      case 'RUNNING':
      case 'IN_PROGRESS':
        return { ...baseStyle, backgroundColor: 'rgba(88, 86, 214, 0.2)', color: '#5856D6', borderColor: '#5856D6' };
      case 'TIMED_OUT':
      case 'ABORTED':
      case 'CANCELLED':
        return { ...baseStyle, backgroundColor: 'rgba(255, 149, 0, 0.2)', color: '#FF9500', borderColor: '#FF9500' };
      default:
        return { ...baseStyle, backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.3)' };
    }
  };

  const getStepCircleColor = (status: string) => {
    switch (status) {
      case 'SUCCEEDED': return Colors.success;
      case 'FAILED': return Colors.error;
      case 'IN_PROGRESS': return '#5856D6';
      case 'CANCELLED': return '#FF9500';
      default: return 'rgba(255, 255, 255, 0.3)';
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (duration?: string) => {
    if (!duration) return '-';
    const seconds = parseFloat(duration);
    if (seconds < 60) {
      return `${seconds.toFixed(3)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = (seconds % 60).toFixed(3);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const hasRunningExecutions = executions.some(e => e.status === 'RUNNING');

  const renderStepsTimeline = (arn: string) => {
    const steps = stepsData[arn];

    if (stepsLoading && !steps) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '20px 0' }}>
          <span className="loading-spinner" />
          <span style={{ fontSize: '13px', color: '#35539d' }}>Loading execution steps...</span>
        </div>
      );
    }

    if (stepsError && !steps) {
      return (
        <div style={{
          padding: '12px 16px',
          backgroundColor: withAlpha(Colors.error, 0.1),
          border: `1px solid ${withAlpha(Colors.error, 0.3)}`,
          borderRadius: '6px',
          color: Colors.error,
          fontSize: '13px',
        }}>
          {stepsError}
        </div>
      );
    }

    if (!steps || steps.length === 0) {
      return (
        <div style={{ padding: '16px 0', color: '#35539d)', fontSize: '13px' }}>
          No steps found for this execution.
        </div>
      );
    }

    return (
      <div style={{ padding: '16px 0' }}>
        {steps.map((step, index) => {
          const circleColor = getStepCircleColor(step.status);
          const isLast = index === steps.length - 1;

          return (
            <div key={`${step.step_name}-${index}`} style={{ display: 'flex', gap: '16px' }}>
              {/* Timeline column */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '20px', flexShrink: 0 }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: circleColor,
                  border: `2px solid ${circleColor}`,
                  flexShrink: 0,
                  marginTop: '4px',
                  boxShadow: step.status === 'IN_PROGRESS' ? `0 0 8px ${circleColor}` : 'none',
                }} />
                {!isLast && (
                  <div style={{
                    width: '2px',
                    flexGrow: 1,
                    backgroundColor: 'rgba(58, 91, 174, 0.15)',
                    minHeight: '24px',
                  }} />
                )}
              </div>

              {/* Step content */}
              <div style={{ flex: 1, paddingBottom: isLast ? 0 : '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ color: '#35539d', fontWeight: 500, fontSize: '13px' }}>
                    {step.step_name}
                  </span>
                  <span style={getStatusStyle(step.status)}>
                    {getStatusIcon(step.status)}
                    {step.status}
                  </span>
                  {step.duration && (
                    <span style={{ fontSize: '12px', color: '#35539d', fontFamily: "'SF Mono', monospace" }}>
                      {formatDuration(step.duration)}
                    </span>
                  )}
                </div>

                {/* Error box for failed steps */}
                {step.status === 'FAILED' && (step.error || step.cause) && (
                  <div style={{
                    marginTop: '8px',
                    padding: '10px 14px',
                    backgroundColor: withAlpha(Colors.error, 0.1),
                    border: `1px solid ${withAlpha(Colors.error, 0.3)}`,
                    borderRadius: '6px',
                    fontSize: '12px',
                  }}>
                    {step.error && (
                      <div style={{ color: Colors.error, fontWeight: 600, marginBottom: step.cause ? '6px' : 0 }}>
                        {step.error}
                      </div>
                    )}
                    {step.cause && (
                      <div style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: "'SF Mono', monospace",
                        fontSize: '11px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}>
                        {step.cause}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="section">
      <h2 className="section-title">
        <Activity size={24} />
        Step Functions Executions
      </h2>

      <Card
        title="Execution History"
        description="View all Step Functions executions and their status"
        headerAction={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {hasRunningExecutions && (
              <span style={{
                fontSize: '12px',
                color: '#5856D6',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                backgroundColor: 'rgba(88, 86, 214, 0.15)',
                borderRadius: '999px',
              }}>
                <span style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#5856D6',
                  animation: 'pulse 2s infinite',
                }} />
                Auto-polling
              </span>
            )}
            <Button
              onClick={fetchExecutions}
              loading={loading}
              className='btn-small btn-primary'
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              <RefreshCw size={14} />
              Refresh
            </Button>
          </div>
        }
      >
        {/* Filters and Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: Colors.text }}>Status:</label>
            <select
              className="form-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              style={{ width: 'auto', padding: '8px 32px 8px 12px', fontSize: '13px' }}
            >
              <option value="ALL">All</option>
              <option value="RUNNING">Running</option>
              <option value="SUCCEEDED">Succeeded</option>
              <option value="FAILED">Failed</option>
              <option value="TIMED_OUT">Timed Out</option>
              <option value="ABORTED">Aborted</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: Colors.text,
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: '#5856D6',
                }}
              />
              Auto-refresh (10s)
            </label>

            {lastRefresh && (
              <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                Last: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-warning">
            <AlertCircle size={20} />
            <div>{error}</div>
          </div>
        )}

        {/* Executions Table */}
        <div style={{
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
        }}>
          <table className="table" style={{ marginBottom: 0 }}>
            <thead>
              <tr style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                <th style={{ width: '32px', padding: '12px 8px 12px 12px' , color: Colors.text}}></th>
                <th>Name</th>
                <th>Status</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {executions.length === 0 ? (
                <tr
                  style={{
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                        }}
                >
                  <td colSpan={6} style={{
                    padding: '40px 16px',
                    textAlign: 'center',
                    color: Colors.text,
                  }}>
                    {loading ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <span className="loading-spinner" />
                        Loading executions...
                      </div>
                    ) : (
                      <div>
                        <Activity size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                        <p>No executions found</p>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                executions.map((execution) => {
                  const isExpanded = expandedArn === execution.execution_arn;
                  return (
                    <React.Fragment key={execution.execution_arn}>
                      <tr
                        onClick={() => handleRowClick(execution)}
                        style={{
                          backgroundColor: '#fff',
                          // isExpanded
                          //   ? '#fff'
                          //   : execution.status === 'RUNNING'
                          //     ? '#fff'
                          //     : '#fff',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!isExpanded) {
                            e.currentTarget.style.backgroundColor = '#f0f3fa';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                          // isExpanded
                          //   ? 'rgba(88, 86, 214, 0.15)'
                          //   : execution.status === 'RUNNING'
                          //     ? 'rgba(88, 86, 214, 0.1)'
                          //     : 'transparent';
                        }}
                      >
                        <td style={{ padding: '12px 8px 12px 12px', width: '32px' }}>
                          {isExpanded
                            ? <ChevronDown size={16} style={{ color: Colors.text }} />
                            : <ChevronRight size={16} style={{ color: Colors.text }} />
                          }
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{
                              color: Colors.text,
                              fontWeight: 500,
                              fontSize: '13px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '280px',
                            }}>
                              {execution.name}
                            </span>
                            {execution.game_name && (
                              <span style={{ fontSize: '11px', color: Colors.text }}>
                                {execution.game_name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span style={getStatusStyle(execution.status)}>
                            {getStatusIcon(execution.status)}
                            {execution.status}
                          </span>
                        </td>
                        <td style={{ fontSize: '13px' }}>
                          {formatDate(execution.start_date)}
                        </td>
                        <td style={{ fontSize: '13px' }}>
                          {formatDate(execution.stop_date)}
                        </td>
                        <td style={{ fontSize: '13px', fontFamily: "'SF Mono', monospace" }}>
                          {formatDuration(execution.duration)}
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} style={{
                            padding: '0 16px 16px 48px',
                            backgroundColor: '#fff',
                            borderBottom: '1px solid rgba(88, 86, 214, 0.2)',
                          }}>
                            {renderStepsTimeline(execution.execution_arn)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          gap: '24px',
          fontSize: '13px',
          flexWrap: 'wrap',
        }}>
          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Total: <strong style={{ color: '#FFFFFF' }}>{executions.length}</strong>
          </span>
          <span style={{ color: Colors.success }}>
            Succeeded: <strong>{executions.filter(e => e.status === 'SUCCEEDED').length}</strong>
          </span>
          <span style={{ color: Colors.error }}>
            Failed: <strong>{executions.filter(e => e.status === 'FAILED').length}</strong>
          </span>
          <span style={{ color: '#5856D6' }}>
            Running: <strong>{executions.filter(e => e.status === 'RUNNING').length}</strong>
          </span>
        </div>
      </Card>
    </div>
  );
}
